/**
 * Service de gestion hors ligne pour les simulateurs BATICO et DOMIUM
 * Utilise IndexedDB pour stocker les données localement
 * Synchronise automatiquement à la reconnexion
 */

const DB_NAME = 'domium_offline_db';
const DB_VERSION = 1;
const STORES = {
  SIMULATIONS: 'simulations',
  FTPE_DRAFTS: 'ftpe_drafts',
  PENDING_SYNC: 'pending_sync'
};

let db = null;

// Initialiser IndexedDB
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Store pour les simulations BATICO/DOMIUM
      if (!database.objectStoreNames.contains(STORES.SIMULATIONS)) {
        const simulationsStore = database.createObjectStore(STORES.SIMULATIONS, { keyPath: 'id' });
        simulationsStore.createIndex('type', 'type', { unique: false });
        simulationsStore.createIndex('created_at', 'created_at', { unique: false });
        simulationsStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les brouillons FTPE
      if (!database.objectStoreNames.contains(STORES.FTPE_DRAFTS)) {
        const ftpeStore = database.createObjectStore(STORES.FTPE_DRAFTS, { keyPath: 'id' });
        ftpeStore.createIndex('created_at', 'created_at', { unique: false });
        ftpeStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les opérations en attente de sync
      if (!database.objectStoreNames.contains(STORES.PENDING_SYNC)) {
        const pendingStore = database.createObjectStore(STORES.PENDING_SYNC, { keyPath: 'id', autoIncrement: true });
        pendingStore.createIndex('type', 'type', { unique: false });
        pendingStore.createIndex('created_at', 'created_at', { unique: false });
      }
    };
  });
};

// Sauvegarder une simulation localement
export const saveSimulationOffline = async (simulation) => {
  try {
    await initOfflineDB();
    
    const data = {
      ...simulation,
      id: simulation.id || `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: simulation.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      synced: false,
      offline_created: !navigator.onLine
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SIMULATIONS], 'readwrite');
      const store = transaction.objectStore(STORES.SIMULATIONS);
      const request = store.put(data);

      request.onsuccess = () => {
        
        // Ajouter à la file de sync si hors ligne
        if (!navigator.onLine) {
          addToPendingSync('simulation', data);
        }
        
        resolve(data);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    throw error;
  }
};

// Récupérer toutes les simulations locales
export const getLocalSimulations = async (type = null) => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SIMULATIONS], 'readonly');
      const store = transaction.objectStore(STORES.SIMULATIONS);
      
      let request;
      if (type) {
        const index = store.index('type');
        request = index.getAll(type);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch {
    return [];
  }
};

// Récupérer une simulation par ID
export const getLocalSimulation = async (id) => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SIMULATIONS], 'readonly');
      const store = transaction.objectStore(STORES.SIMULATIONS);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch {
    return null;
  }
};

// Supprimer une simulation locale
export const deleteLocalSimulation = async (id) => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SIMULATIONS], 'readwrite');
      const store = transaction.objectStore(STORES.SIMULATIONS);
      const request = store.delete(id);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch {
    return false;
  }
};

// Sauvegarder un brouillon FTPE localement
export const saveFTPEDraftOffline = async (draft) => {
  try {
    await initOfflineDB();
    
    const data = {
      ...draft,
      id: draft.id || `ftpe_offline_${Date.now()}`,
      created_at: draft.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      synced: false,
      offline_created: !navigator.onLine
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.FTPE_DRAFTS], 'readwrite');
      const store = transaction.objectStore(STORES.FTPE_DRAFTS);
      const request = store.put(data);

      request.onsuccess = () => {
        
        if (!navigator.onLine) {
          addToPendingSync('ftpe_draft', data);
        }
        
        resolve(data);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    throw error;
  }
};

// Récupérer les brouillons FTPE locaux
export const getLocalFTPEDrafts = async () => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.FTPE_DRAFTS], 'readonly');
      const store = transaction.objectStore(STORES.FTPE_DRAFTS);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch {
    return [];
  }
};

// Ajouter une opération à la file de synchronisation
const addToPendingSync = async (type, data) => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      
      const syncItem = {
        type,
        data,
        created_at: new Date().toISOString(),
        attempts: 0
      };

      const request = store.add(syncItem);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[offlineService] addPendingSyncItem failed:', error);
  }
};

// Récupérer les opérations en attente de sync
export const getPendingSyncItems = async () => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch {
    return [];
  }
};

// Supprimer un item de la file de sync
const removePendingSyncItem = async (id) => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      const request = store.delete(id);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch {
    return false;
  }
};

// Marquer une simulation comme synchronisée
export const markAsSynced = async (id, storeName = STORES.SIMULATIONS) => {
  try {
    await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const data = getRequest.result;
        if (data) {
          data.synced = true;
          data.synced_at = new Date().toISOString();
          const putRequest = store.put(data);
          
          putRequest.onsuccess = () => resolve(true);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(false);
        }
      };

      getRequest.onerror = () => {
        reject(getRequest.error);
      };
    });
  } catch {
    return false;
  }
};

// Synchroniser toutes les données en attente
export const syncAllPendingData = async (apiUrl, token) => {
  if (!navigator.onLine) {
    return { success: false, reason: 'offline' };
  }

  const results = {
    simulations: { synced: 0, failed: 0 },
    ftpe_drafts: { synced: 0, failed: 0 },
    total_pending: 0
  };

  try {
    const pendingItems = await getPendingSyncItems();
    results.total_pending = pendingItems.length;

    if (pendingItems.length === 0) {
      return { success: true, results };
    }


    for (const item of pendingItems) {
      try {
        let endpoint = '';
        let method = 'POST';

        switch (item.type) {
          case 'simulation':
            endpoint = `${apiUrl}/souscriptions/simulation`;
            break;
          case 'ftpe_draft':
            endpoint = `${apiUrl}/ftpe-adhesion/workflow/save-draft`;
            break;
          default:
            continue;
        }

        const response = await fetch(endpoint, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(item.data)
        });

        if (response.ok) {
          const _responseData = await response.json();
          
          // Marquer comme synchronisé
          if (item.type === 'simulation') {
            await markAsSynced(item.data.id, STORES.SIMULATIONS);
            results.simulations.synced++;
          } else if (item.type === 'ftpe_draft') {
            await markAsSynced(item.data.id, STORES.FTPE_DRAFTS);
            results.ftpe_drafts.synced++;
          }
          
          // Supprimer de la file de sync
          await removePendingSyncItem(item.id);
          
        } else {
          
          // Incrémenter le compteur de tentatives
          item.attempts = (item.attempts || 0) + 1;
          
          if (item.type === 'simulation') {
            results.simulations.failed++;
          } else {
            results.ftpe_drafts.failed++;
          }
        }
      } catch {
      }
    }

    return { success: true, results };

  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Obtenir le nombre d'éléments non synchronisés
export const getUnsyncedCount = async () => {
  try {
    const pending = await getPendingSyncItems();
    return pending.length;
  } catch {
    return 0;
  }
};

// Nettoyer les anciennes données (plus de 30 jours)
export const cleanupOldData = async (daysOld = 30) => {
  try {
    await initOfflineDB();
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    const cutoffIso = cutoffDate.toISOString();

    let deletedCount = 0;

    // Nettoyer les simulations anciennes synchronisées
    const simulations = await getLocalSimulations();
    for (const sim of simulations) {
      if (sim.synced && sim.created_at < cutoffIso) {
        await deleteLocalSimulation(sim.id);
        deletedCount++;
      }
    }

    return deletedCount;
  } catch {
    return 0;
  }
};

export default {
  initOfflineDB,
  saveSimulationOffline,
  getLocalSimulations,
  getLocalSimulation,
  deleteLocalSimulation,
  saveFTPEDraftOffline,
  getLocalFTPEDrafts,
  getPendingSyncItems,
  syncAllPendingData,
  getUnsyncedCount,
  markAsSynced,
  cleanupOldData
};
